import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  setActiveOrganization,
  selectHasMultipleOrgs,
} from "../store/userSlice";
import "./OrgSelector.css";

/**
 * Organization Selector Component
 * Displays when user has multiple organization memberships
 * Allows switching between organizations
 */
function OrgSelector({ isHeader = false }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const hasMultipleOrgs = useSelector(selectHasMultipleOrgs);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleOrgSwitch = (membership) => {
    dispatch(
      setActiveOrganization({ organizationId: membership.organization_id })
    );
    setIsOpen(false);

    // Store selection in localStorage
    localStorage.setItem("activeOrgId", membership.organization_id);

    // Navigate to dashboard to trigger re-render with new org context
    navigate("/dashboard");
  };

  // Don't show if user has only one org
  if (!hasMultipleOrgs) {
    return null;
  }

  return (
    <div
      className={`org-selector ${isHeader ? "org-selector-header" : ""}`}
      ref={dropdownRef}
    >
      <button
        className="org-selector-button"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="org-name">{user.organizationName}</span>
        <span className="org-role">{user.orgRole}</span>
        <span className="dropdown-icon">{isOpen ? "▲" : "▼"}</span>
      </button>

      {isOpen && (
        <div className="org-selector-dropdown">
          <div className="dropdown-header">Switch Organization</div>
          {user.memberships.map((membership) => (
            <button
              key={membership.organization_id}
              className={`org-option ${
                membership.organization_id === user.activeOrgId ? "active" : ""
              }`}
              onClick={() => handleOrgSwitch(membership)}
            >
              <div className="org-option-name">
                {membership.organization_name}
              </div>
              <div className="org-option-role">{membership.role}</div>
              {membership.organization_id === user.activeOrgId && (
                <span className="active-indicator">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default OrgSelector;
